import React, { useState, useMemo } from 'react';
import { formatRupiah } from '../data/financialData';
import type { Area } from '../data/financialData';
import { useData } from '../DataContext';
import { useTheme } from '../ThemeContext';
import { parseGLRows, GLRow } from '../utils/budgetMatch';

interface OpenTab {
  branch: string;
  area: string;
}

interface RevenueDetailPageProps {
  area: Area;
  branch: string;
  startIdx: number;
  endIdx: number;
  activeEntity?: string;
}

const TabContent: React.FC<{
  branch: string;
  startIdx: number;
  endIdx: number;
  onDrilldown: (title: string, branch: string, area: string | null, monthList: string[]) => void;
  monthsList: { month: string; label: string }[];
}> = ({ branch, startIdx, endIdx, onDrilldown, monthsList }) => {
  const { getRevenueExpensesSeries } = useData();
  const seriesData = useMemo(
    () => getRevenueExpensesSeries(branch, startIdx, endIdx),
    [branch, startIdx, endIdx, getRevenueExpensesSeries]
  );

  return (
    <div style={{ marginBottom: '16px' }}>
      <div style={{ fontSize: '12px', fontWeight: 'bold', marginBottom: '8px', color: 'var(--text-primary)' }}>
        {branch} — Revenue + OpEx + Net Profit (Klik nominal Revenue untuk rincian)
      </div>
      <table className="data-table" style={{ fontSize: '12px', width: '100%' }}>
        <thead>
          <tr>
            <th style={{ textAlign: 'left' }}>Bulan</th>
            <th style={{ textAlign: 'right' }}>Revenue</th>
            <th style={{ textAlign: 'right' }}>OpEx</th>
            <th style={{ textAlign: 'right' }}>Net Profit</th>
          </tr>
        </thead>
        <tbody>
          {seriesData.labels.map((label, i) => {
            const mInfo = monthsList[i];
            const monthCode = mInfo ? mInfo.month : '';
            return (
              <tr key={i}>
                <td style={{ textAlign: 'left', color: 'var(--text-secondary)' }}>{label}</td>
                <td style={{ textAlign: 'right' }}>
                  <span
                    className="drillable-value"
                    onClick={() => monthCode && onDrilldown(`Revenue ${branch} - ${label}`, branch, null, [monthCode])}
                  >
                    {formatRupiah(seriesData.revenue[i])}
                  </span>
                </td>
                <td style={{ textAlign: 'right' }}>{formatRupiah(seriesData.opex[i])}</td>
                <td style={{ textAlign: 'right', color: seriesData.revenue[i] - seriesData.opex[i] >= 0 ? '#10b981' : '#ef4444' }}>
                  {formatRupiah(seriesData.revenue[i] - seriesData.opex[i])}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

const RevenueDetailPage: React.FC<RevenueDetailPageProps> = ({ area, branch, startIdx, endIdx, activeEntity }) => {
  const { MONTHS, getRevenueByBranch } = useData();
  const { chart: chartTheme } = useTheme();
  
  const [expandedArea, setExpandedArea] = useState<string | null>('Depok');
  const [openTabs, setOpenTabs] = useState<OpenTab[]>([]);

  // Drilldown states
  const [glData, setGlData] = useState<{ columns: string[]; data: any[][] } | null>(null);
  const [glLoading, setGlLoading] = useState(false);
  const [drilldownTitle, setDrilldownTitle] = useState<string>('');
  const [drilldownRows, setDrilldownRows] = useState<GLRow[]>([]);
  const [showModal, setShowModal] = useState(false);

  // Active Entity Color Palette
  const accentColor = activeEntity === 'JUARA_GADAI' ? '#F68B1F' : '#10b981';
  const accentLight = activeEntity === 'JUARA_GADAI' ? '#FBA94D' : '#34d399';
  const accentGlow = activeEntity === 'JUARA_GADAI' ? 'rgba(246, 139, 31, 0.15)' : 'rgba(16, 185, 129, 0.15)';

  const periodMonths = useMemo(() => MONTHS.slice(startIdx, endIdx + 1), [MONTHS, startIdx, endIdx]);

  const branchData = useMemo(() => getRevenueByBranch(startIdx, endIdx), [startIdx, endIdx, getRevenueByBranch]);
  const byArea = useMemo(() => {
    const grouped: Record<string, typeof branchData> = {};
    for (const item of branchData) {
      if (!grouped[item.area]) grouped[item.area] = [];
      grouped[item.area].push(item);
    }
    return grouped;
  }, [branchData]);

  const handleBranchClick = (br: string, ar: string) => {
    if (!openTabs.find(t => t.branch === br)) {
      setOpenTabs([...openTabs, { branch: br, area: ar }]);
    }
  };

  const handleCloseTab = (br: string) => {
    setOpenTabs(openTabs.filter(t => t.branch !== br));
  };

  const handleDrilldown = async (
    title: string,
    branchFilter: string,
    areaFilter: string | null,
    monthList: string[]
  ) => {
    setDrilldownTitle(title);
    setDrilldownRows([]);
    setGlLoading(true);
    setShowModal(true);

    try {
      const API_KEY = process.env.REACT_APP_BUDGET_API_KEY ?? '';
      const API_BASE = process.env.REACT_APP_BUDGET_API_BASE ?? '';

      const qParams = new URLSearchParams({
        entity: activeEntity ?? 'JUARA_GADAI',
        months: monthList.join(','),
        acc_code_prefixes: '10'
      });
      if (branchFilter !== 'ALL') {
        qParams.set('branch', branchFilter);
      } else if (areaFilter && areaFilter !== 'ALL') {
        qParams.set('area', areaFilter);
      }

      const res = await fetch(`${API_BASE}/api/gl-transactions?${qParams.toString()}`, {
        headers: { 'Authorization': `Bearer ${API_KEY}` }
      });
      if (!res.ok) throw new Error('Gagal memuat detail jurnal Buku Besar dari cloud.');
      const loadedData = await res.json();
      const parsedAll = parseGLRows(loadedData.data);

      setDrilldownRows(parsedAll);
    } catch (err: any) {
      console.error(err);
      alert(err.message || 'Terjadi kesalahan saat memuat drilldown.');
      setShowModal(false);
    } finally {
      setGlLoading(false);
    }
  };

  return (
    <div className="data-table-card animate-in">
      <style dangerouslySetInnerHTML={{__html: `
        .drillable-value {
          font-family: 'monospace';
          cursor: pointer;
          position: relative;
          transition: all 0.2s;
          padding: 2px 6px;
          border-radius: 4px;
          color: inherit;
          white-space: nowrap;
        }
        .drillable-value:hover {
          color: ${accentColor} !important;
          background: ${accentGlow};
          text-decoration: underline;
        }
        .drill-modal-overlay {
          position: fixed;
          inset: 0;
          left: 260px;
          background: rgba(15, 23, 42, 0.7);
          backdrop-filter: blur(8px);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
          animation: fadeIn 0.25s ease;
          transition: left var(--transition-normal);
        }
        .main-content.sidebar-collapsed .drill-modal-overlay {
          left: 80px;
        }
        @media (max-width: 768px) {
          .drill-modal-overlay {
            left: 0 !important;
          }
        }
        .drill-modal {
          background: var(--bg-card);
          border: 1px solid var(--border-color);
          border-radius: 16px;
          padding: 28px;
          width: 900px;
          max-width: 92vw;
          max-height: 80vh;
          box-shadow: var(--shadow-2xl);
          display: flex;
          flex-direction: column;
          gap: 16px;
          animation: slideUp 0.25s cubic-bezier(0.16, 1, 0.3, 1);
        }
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes slideUp { from { transform: translateY(20px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
        @keyframes spin { to { transform: rotate(360deg); } }
      `}} />

      <div className="data-table-header">
        <div>
          <div className="data-table-title">💰 Revenue Detail</div>
          <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '2px' }}>
            Rekap nominal percabang dan area — {MONTHS[startIdx]?.label || ''} – {MONTHS[endIdx]?.label || ''} (Klik nominal untuk drilldown Buku Besar)
          </div>
        </div>
      </div>

      {openTabs.length > 0 && (
        <div style={{
          display: 'flex',
          gap: '4px',
          borderBottom: '1px solid var(--border-color)',
          padding: '8px 16px',
          overflow: 'auto',
          backgroundColor: 'var(--bg-secondary)',
        }}>
          {openTabs.map(tab => (
            <div key={tab.branch} style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              backgroundColor: 'var(--bg-primary)',
              border: '1px solid var(--border-color)',
              borderRadius: '4px 4px 0 0',
              padding: '6px 12px',
              fontSize: '12px',
              cursor: 'pointer',
            }}>
              <span>{tab.branch}</span>
              <button
                onClick={() => handleCloseTab(tab.branch)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: 'var(--text-muted)',
                  cursor: 'pointer',
                  fontSize: '14px',
                }}
              >
                ×
              </button>
            </div>
          ))}
        </div>
      )}

      {openTabs.length > 0 && (
        <div style={{ padding: '12px 16px', backgroundColor: 'var(--bg-secondary)' }}>
          {openTabs.map(tab => (
            <TabContent
              key={tab.branch}
              branch={tab.branch}
              startIdx={startIdx}
              endIdx={endIdx}
              onDrilldown={handleDrilldown}
              monthsList={periodMonths}
            />
          ))}
        </div>
      )}

      <div style={{ overflowX: 'auto', padding: '16px' }}>
        <table className="data-table" id="revenue-detail-table">
          <thead>
            <tr>
              <th style={{ textAlign: 'left' }}>Cabang</th>
              <th style={{ textAlign: 'right' }}>Total Periode</th>
              <th style={{ textAlign: 'right' }}>Avg/Bulan</th>
              <th style={{ textAlign: 'right' }}>% Area</th>
              <th style={{ textAlign: 'center' }}>Trend</th>
            </tr>
          </thead>
          <tbody>
            {Object.entries(byArea).map(([areaName, branches]) => (
              <React.Fragment key={areaName}>
                <tr
                  onClick={() => setExpandedArea(expandedArea === areaName ? null : areaName)}
                  style={{
                    backgroundColor: 'var(--bg-secondary)',
                    fontWeight: 'bold',
                    cursor: 'pointer',
                  }}
                >
                  <td style={{ paddingLeft: '16px' }}>
                    <span style={{ marginRight: '8px' }}>
                      {expandedArea === areaName ? '▼' : '▶'}
                    </span>
                    {areaName} ({branches.length} cabang)
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    <span
                      className="drillable-value"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDrilldown(`Revenue Area ${areaName}`, 'ALL', areaName, periodMonths.map(m => m.month));
                      }}
                    >
                      {formatRupiah(branches.reduce((s, b) => s + b.totalPeriod, 0))}
                    </span>
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    <span
                      className="drillable-value"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDrilldown(`Revenue Area ${areaName}`, 'ALL', areaName, periodMonths.map(m => m.month));
                      }}
                    >
                      {formatRupiah(branches.reduce((s, b) => s + b.totalPeriod, 0) / branches.length)}
                    </span>
                  </td>
                  <td style={{ textAlign: 'right' }}>100%</td>
                  <td />
                </tr>
                {expandedArea === areaName &&
                  branches.map(item => (
                    <tr key={item.branch}>
                      <td style={{ paddingLeft: '32px', cursor: 'pointer' }} onClick={() => handleBranchClick(item.branch, item.area)}>
                        <span className="category-badge">{item.branch}</span>
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        <span
                          className="drillable-value"
                          onClick={() => handleDrilldown(`Revenue ${item.branch}`, item.branch, null, periodMonths.map(m => m.month))}
                        >
                          {formatRupiah(item.totalPeriod)}
                        </span>
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        <span
                          className="drillable-value"
                          onClick={() => handleDrilldown(`Revenue ${item.branch}`, item.branch, null, periodMonths.map(m => m.month))}
                        >
                          {formatRupiah(item.avgPerMonth)}
                        </span>
                      </td>
                      <td style={{ textAlign: 'right' }}>{item.pctOfArea.toFixed(1)}%</td>
                      <td style={{ textAlign: 'center', color: item.trend === 'up' ? '#10b981' : item.trend === 'down' ? '#ef4444' : '#94a3b8' }}>
                        {item.trend === 'up' ? '↑' : item.trend === 'down' ? '↓' : '—'}
                      </td>
                    </tr>
                  ))}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      {/* DRILLDOWN JURNAL MODAL POPUP */}
      {showModal && (
        <div className="drill-modal-overlay" onClick={() => setShowModal(false)}>
          <div className="drill-modal" onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', borderBottom: '1px solid var(--border-color)', paddingBottom: '12px' }}>
              <div>
                <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '800', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span>📖</span> Drilldown Jurnal Pendapatan
                </h3>
                <p style={{ fontSize: '11px', color: 'var(--text-secondary)', marginTop: '4px' }}>
                  Detail baris transaksi pendapatan untuk: <strong>{drilldownTitle}</strong>
                </p>
              </div>
              <button 
                onClick={() => setShowModal(false)}
                style={{ background: 'none', border: 'none', color: 'var(--text-muted)', fontSize: '18px', cursor: 'pointer' }}
              >
                ✕
              </button>
            </div>

            {glLoading ? (
              <div style={{ padding: '60px', textAlign: 'center' }}>
                <div className="spinner" style={{ margin: '0 auto 16px', width: '36px', height: '36px', borderRadius: '50%', border: '3px solid var(--border-light)', borderTopColor: accentColor, animation: 'spin 1s linear infinite' }} />
                <div style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>Mengekstrak baris jurnal transaksi Buku Besar...</div>
              </div>
            ) : drilldownRows.length === 0 ? (
              <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-secondary)' }}>
                Tidak ditemukan baris transaksi Buku Besar Pendapatan yang cocok pada filter periode/cabang ini.
              </div>
            ) : (
              <div style={{ overflowY: 'auto', flex: 1, maxHeight: '50vh' }}>
                <table className="data-table" style={{ width: '100%', fontSize: '12px' }}>
                  <thead style={{ position: 'sticky', top: 0, background: 'var(--bg-card)', zIndex: 1 }}>
                    <tr>
                      <th style={{ textAlign: 'left' }}>Bulan</th>
                      <th style={{ textAlign: 'left' }}>Nama Akun (COA)</th>
                      <th style={{ textAlign: 'left' }}>Keterangan / Deskripsi</th>
                      <th style={{ textAlign: 'left' }}>Cabang</th>
                      <th style={{ textAlign: 'right' }}>Debit</th>
                      <th style={{ textAlign: 'right' }}>Kredit</th>
                      <th style={{ textAlign: 'right' }}>Net Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {drilldownRows.map((row, idx) => (
                      <tr key={idx}>
                        <td style={{ whiteSpace: 'nowrap' }}>{row.month}</td>
                        <td style={{ fontWeight: '600' }}>{row.accName}</td>
                        <td style={{ color: 'var(--text-accent)', maxWidth: '280px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={row.desc}>
                          {row.desc}
                        </td>
                        <td style={{ fontFamily: 'monospace', fontWeight: '700' }}>{row.branch}</td>
                        <td style={{ textAlign: 'right', fontFamily: 'monospace' }}>{row.debit > 0 ? formatRupiah(row.debit) : '—'}</td>
                        <td style={{ textAlign: 'right', fontFamily: 'monospace' }}>{row.credit > 0 ? formatRupiah(row.credit) : '—'}</td>
                        <td style={{ textAlign: 'right', fontFamily: 'monospace', fontWeight: '700', color: row.amount >= 0 ? 'var(--emerald-light)' : 'var(--red-light)' }}>
                          {formatRupiah(row.amount)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <div style={{ borderTop: '1px solid var(--border-color)', paddingTop: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '12px' }}>
              <span style={{ color: 'var(--text-secondary)' }}>
                Ditemukan <strong>{drilldownRows.length}</strong> baris transaksi jurnal.
              </span>
              <button className="btn btn-secondary" onClick={() => setShowModal(false)} style={{ padding: '6px 14px', fontSize: '12px' }}>
                Tutup Rincian
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RevenueDetailPage;
